import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader, Loading } from "@/components/ui-kit";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CheckCircle2, FileText, Tag, Eye, EyeOff } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function MistakeBank() {
  const [all, setAll] = useState(null);
  const [tag, setTag] = useState("all");
  const [reveal, setReveal] = useState({});

  useEffect(() => { api.get("/student/mistakes").then((r) => setAll(r.data)); }, []);
  const tags = useMemo(() => [...new Set((all || []).flatMap((m) => m.tags || []))].sort(), [all]);
  if (!all) return <Loading />;

  const filtered = all.filter((m) => tag === "all" || (m.tags || []).includes(tag));
  // group by exam/test
  const groups = {};
  filtered.forEach((m) => {
    const key = m.homework_id || m.attempt_id || "ungrouped";
    groups[key] = groups[key] || { title: m.title, section: m.section_name, program: m.program_name, week: m.week, file_url: m.file_url, attempt_id: m.attempt_id, items: [] };
    groups[key].items.push(m);
  });

  return (
    <div className="animate-fade-up">
      <PageHeader title="My Mistake Bank" subtitle="Your mistakes, grouped by the exam they came from. Open the paper to re-answer, then check against the key."
        right={
          <Select value={tag} onValueChange={setTag}>
            <SelectTrigger data-testid="mb-tag-filter" className="h-10 w-56 rounded-xl"><SelectValue placeholder="All topics" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All topics / tags</SelectItem>{tags.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
          </Select>
        } />

      {Object.keys(groups).length === 0 ? (
        <div className="ember-card grid place-items-center py-16 text-center"><CheckCircle2 className="h-10 w-10 text-[#059669]" /><p className="mt-3 font-semibold text-[#524B6B]">No mistakes here — great work!</p></div>
      ) : (
        <div className="grid gap-5">
          {Object.entries(groups).map(([gid, g]) => (
            <div key={gid} className="ember-card overflow-hidden" data-testid={`mb-exam-${gid}`}>
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[#F1EEFA] p-5">
                <div>
                  <h3 className="font-heading font-bold text-[#1E1B2E]">{g.title}</h3>
                  <p className="text-xs text-[#948EAB]">{g.program} · {g.section} · <b className="text-[#E11D48]">{g.items.length} to redo</b></p>
                </div>
                {g.file_url && (
                  <a href={`${BACKEND}${g.file_url}`} target="_blank" rel="noreferrer" data-testid={`mb-view-paper-${gid}`}>
                    <Button className="h-10 rounded-xl ember-gradient text-xs font-bold"><FileText className="mr-1.5 h-4 w-4" /> Open exam paper</Button>
                  </a>
                )}
              </div>
              <div className="divide-y divide-[#F5F3FF]">
                {g.items.map((m, i) => {
                  const rk = `${gid}-${m.qid}-${i}`;
                  const shown = reveal[rk];
                  return (
                    <div key={rk} className="flex flex-wrap items-center gap-3 p-4" data-testid={`mb-q-${m.number}`}>
                      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-[#FFF1F2] font-heading font-bold text-[#E11D48]">{m.number}</span>
                      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5">
                        {(m.tags || []).length > 0 ? [...new Set(m.tags)].map((t) => (
                          <span key={t} className="inline-flex items-center gap-1 rounded-full bg-[#EDE9FE] px-2.5 py-1 text-[11px] font-bold text-[#5B21B6]"><Tag className="h-3 w-3" /> {t}</span>
                        )) : <span className="text-xs text-[#948EAB]">{m.section_name}</span>}
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <span className="rounded-lg bg-[#FFF1F2] px-2 py-1 font-bold text-[#E11D48]" data-testid={`mb-your-${m.number}`}>You: {m.your_answer || "—"}</span>
                        {shown
                          ? <span className="rounded-lg bg-[#ECFDF5] px-2 py-1 font-bold text-[#059669]" data-testid={`mb-correct-${m.number}`}>Correct: {m.correct_answer || "—"}</span>
                          : <button onClick={() => setReveal({ ...reveal, [rk]: true })} data-testid={`mb-reveal-${m.number}`} className="inline-flex items-center gap-1 rounded-lg border border-[#DDD6FE] px-2 py-1 text-xs font-bold text-[#7C3AED] hover:bg-[#F5F3FF]"><Eye className="h-3.5 w-3.5" /> Reveal</button>}
                        {shown && <button onClick={() => setReveal({ ...reveal, [rk]: false })} className="text-[#948EAB] hover:text-[#5B21B6]"><EyeOff className="h-4 w-4" /></button>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
